import React, { useState, useEffect, useCallback } from "react";
import useStyles from "./style";
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  Grid,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
  Box,
  CircularProgress
} from "@mui/material";
import { Visibility, VisibilityOff, EditOutlined, Search as SearchIcon } from "@mui/icons-material";
import { useFormik } from "formik";
import * as yup from "yup";
import _ from "lodash";
import { getCountryCodes } from "Utils/country";
import momenttime from 'moment-timezone';

//Libs
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch } from "react-redux";

//Services
import demoaccountapi from "Services/Demoaccount";

const DemoaccountUpdate = (props) => {
  const { openDialog, id, handleClose } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const [details, setDetails] = useState({});

  const timezones = momenttime.tz.names();
  const [countryCodeData, setCountryCodeData] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [countrySearch, setCountrySearch] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const fetchCountryCodes = async () => {
      try {
        const countries = getCountryCodes();
        setCountryCodeData(countries);
        setLoadingCountries(false);
      } catch (error) {
        console.error('Error fetching country codes:', error);
        setLoadingCountries(false);
      }
    };
    fetchCountryCodes();
  }, []);

  const getDetails = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        setDetails(res?.data?.data);
      } else {
        setDetails({});
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to fetch data",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    if (id) {
      demoaccountapi.getDemoaccountDetails(id).then(onSuccess, onFailure);
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (openDialog) {
      getDetails();
    }
  }, [openDialog, getDetails]);

  const handleUpdatePassengers = (formValues) => {
    dispatch(showLoader("Loading please wait..."));

    const data = {
      firstName: formValues?.firstName,
      country: formValues?.country,
      countryCode: formValues?.countryCode,
      mobileNumber: formValues?.mobileNumber,
      email: formValues?.email,
      password: formValues?.password,
      passConfirmation: formValues?.passConfirmation,
      SellerEmail: formValues?.SellerEmail,
      Address: formValues?.Address,
      TimeZone: formValues?.TimeZone,
      isActive: formValues?.isActive,
    };

    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: "DemoAccount Details Updated Successfully",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "success",
          })
        );
        formik.resetForm();
        handleClose();
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Error while update demo account",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };

    demoaccountapi.updateDemoAccount(id, { ...data }).then(onSuccess, onFailure);
  };

  const formik = useFormik({
    initialValues: {
      firstName: "",
      email: "",
      password: "",
      passConfirmation: "",
      country: "",
      mobileNumber: "",
      countryCode: "",
      SellerEmail: "",
      Address: "",
      TimeZone: "",
      isActive: NaN,
    },
    validationSchema: yup.object({
      firstName: yup.string().required("Full Name is required").nullable(),
      email: yup
        .string()
        .email("Invalid email format")
        .required("Email is required")
        .nullable(),
      countryCode: yup.string().required("Country Code is required").nullable(),
      mobileNumber: yup
        .string()
        .matches(/^\d{1,15}$/, "Invalid mobile number")
        .required("Mobile is required")
        .nullable(),
      password: yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("Password is required")
        .nullable(),
      country: yup.string().required("Country is required").nullable(),
    }),
    enableReinitialize: true,
    onSubmit: handleUpdatePassengers,
  });

  useEffect(() => {
    if (!_.isEmpty(details)) {
      formik.setFieldValue("firstName", details?.firstName || "");
      formik.setFieldValue("countryCode", details?.countryCode || "");
      formik.setFieldValue("mobileNumber", details?.mobileNumber || "");
      formik.setFieldValue("email", details?.email || "");
      formik.setFieldValue("password", details?.showPassword || "");
      formik.setFieldValue("country", details?.country || "");
      formik.setFieldValue("SellerEmail", details?.SellerEmail || "");
      formik.setFieldValue("Address", details?.address || "");
      formik.setFieldValue("TimeZone", details?.timeZone || "");
      formik.setFieldValue("isActive", details?.isActive);
    }
  }, [details]);

  return (
    <Dialog
      open={openDialog}
      onClose={() => {
        formik.resetForm();
        handleClose();
      }}
      classes={{ paper: classes.dialogPaper }}
    >
      <Box className={classes.headerContainer}>
        <Typography className={classes.dialogTitle}>Update Demo Account</Typography>
        <Typography className={classes.breadcrumb}>
          Admin / <span>Demo Account</span>
        </Typography>
      </Box>

      <DialogContent style={{ padding: 0 }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            {/* Full Name */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Full Name</Typography>
              <TextField
                disabled
                name="firstName"
                placeholder="e.g. Sarah Jenkins"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                helperText={formik.touched.firstName && formik.errors.firstName}
                fullWidth
                variant="outlined"
                size="small"
                className={classes.formInput}
              />
            </Grid>

            {/* Email Address */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Email Address</Typography>
              <TextField
                disabled
                name="email"
                placeholder="sarah.j@taxiflow.com"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                fullWidth
                variant="outlined"
                size="small"
                className={classes.formInput}
              />
            </Grid>

            {/* Password */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Password</Typography>
              <TextField
                disabled
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={formik.values.password}
                onChange={formik.handleChange}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                fullWidth
                variant="outlined"
                size="small"
                className={classes.formInput}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Status (isActive) */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Status</Typography>
              <TextField
                select
                name="isActive"
                value={formik.values.isActive !== undefined && !isNaN(formik.values.isActive) ? formik.values.isActive : ""}
                onChange={formik.handleChange}
                error={formik.touched.isActive && Boolean(formik.errors.isActive)}
                helperText={formik.touched.isActive && formik.errors.isActive}
                fullWidth
                variant="outlined"
                size="small"
                className={classes.formInput}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (value) => value !== "" ? (value === 1 ? "Active" : "Inactive") : <span style={{ color: "#94a3b8" }}>Select Status</span>
                }}
              >
                <MenuItem value="" disabled>Select Status</MenuItem>
                <MenuItem value={1}>Active</MenuItem>
                <MenuItem value={0}>Inactive</MenuItem>
              </TextField>
            </Grid>

            {/* Seller Email Id */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Seller Email</Typography>
              <TextField
                disabled
                name="SellerEmail"
                placeholder="seller@domain.com"
                value={formik.values.SellerEmail}
                onChange={formik.handleChange}
                error={formik.touched.SellerEmail && Boolean(formik.errors.SellerEmail)}
                helperText={formik.touched.SellerEmail && formik.errors.SellerEmail}
                fullWidth
                variant="outlined"
                size="small"
                className={classes.formInput}
              />
            </Grid>

            {/* Address */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Address</Typography>
              <TextField
                disabled
                name="Address"
                placeholder="123 Main St"
                value={formik.values.Address}
                onChange={formik.handleChange}
                error={formik.touched.Address && Boolean(formik.errors.Address)}
                helperText={formik.touched.Address && formik.errors.Address}
                fullWidth
                variant="outlined"
                size="small"
                className={classes.formInput}
              />
            </Grid>

            {/* Country */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Country</Typography>
              <TextField
                disabled
                select
                name="country"
                value={formik.values.country}
                onChange={(e) => {
                  formik.handleChange(e);
                  const selectedCountry = countryCodeData.find(c => c.name === e.target.value);
                  if (selectedCountry) {
                    formik.setFieldValue("countryCode", selectedCountry.dial_code);
                  }
                }}
                error={formik.touched.country && Boolean(formik.errors.country)}
                helperText={formik.touched.country && formik.errors.country}
                fullWidth
                variant="outlined"
                size="small"
                className={classes.formInput}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (value) => value || <span style={{ color: "#94a3b8" }}>Select Country</span>,
                  MenuProps: {
                    autoFocus: false,
                    onClose: () => setCountrySearch(""),
                  }
                }}
              >
                <MenuItem value="" disabled sx={{ display: "none" }}>
                  Select Country
                </MenuItem>

                <div style={{ padding: "8px", position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 1 }}>
                  <TextField
                    autoFocus
                    size="small"
                    placeholder="Search country..."
                    fullWidth
                    value={countrySearch}
                    onChange={(e) => setCountrySearch(e.target.value)}
                    onClick={(e) => {
                      // Prevent the Select dropdown from closing when clicking inside
                      e.stopPropagation();
                    }}
                    onKeyDown={(e) => {
                      // Prevent the Select dropdown from closing when typing space and bubbling
                      e.stopPropagation();
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>

                {countryCodeData
                  .filter((c) =>
                    c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
                    c.code.toLowerCase().includes(countrySearch.toLowerCase()) ||
                    c.dial_code.toLowerCase().includes(countrySearch.toLowerCase())
                  )
                  .map((c) => (
                    <MenuItem key={c.code} value={c.name}>
                      {`${c.code} (${c.name})`}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>

            {/* Mobile Number */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Mobile Number</Typography>
              <Box className={classes.mobileInputWrapper}>
                <TextField
                  disabled
                  select
                  name="countryCode"
                  value={formik.values.countryCode}
                  onChange={formik.handleChange}
                  variant="outlined"
                  size="small"
                  style={{ width: "100px" }}
                  className={classes.formInput}
                >
                  {countryCodeData.map((c) => (
                    <MenuItem key={c.code} value={c.dial_code}>
                      {c.dial_code}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  disabled
                  name="mobileNumber"
                  placeholder="000-000-0000"
                  value={formik.values.mobileNumber}
                  onChange={formik.handleChange}
                  error={formik.touched.mobileNumber && Boolean(formik.errors.mobileNumber)}
                  helperText={formik.touched.mobileNumber && formik.errors.mobileNumber}
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={classes.formInput}
                />
              </Box>
            </Grid>

            {/* Time Zone */}
            <Grid item xs={12} md={6}>
              <Typography className={classes.formLabel}>Time Zone</Typography>
              <TextField
                disabled
                select
                name="TimeZone"
                value={formik.values.TimeZone}
                onChange={formik.handleChange}
                error={formik.touched.TimeZone && Boolean(formik.errors.TimeZone)}
                helperText={formik.touched.TimeZone && formik.errors.TimeZone}
                fullWidth
                variant="outlined"
                size="small"
                className={classes.formInput}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (value) => value || <span style={{ color: "#94a3b8" }}>Select Time Zone</span>
                }}
              >
                <MenuItem value="" disabled>Select Time Zone</MenuItem>
                {timezones.map((tz) => (
                  <MenuItem key={tz} value={tz}>{tz}</MenuItem>
                ))}
              </TextField>
            </Grid>

          </Grid>

          <Box className={classes.dialogActions}>
            <Button
              className={classes.cancelButton}
              onClick={() => {
                formik.resetForm();
                handleClose();
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              className={classes.submitButton}
            >
              <EditOutlined style={{ fontSize: "18px" }} />
              Save Changes
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DemoaccountUpdate;
